import React, { useEffect, useState } from "react";
import { Button, LoadingSpinner } from "../Common.jsx";
import { apiFetch } from "../../api/index.js";
import { DynamicIcon } from "../icons.jsx";

export default function FeatureConfigForm({ data, onSave, token, onNext }) {
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    enabled: Array.isArray(data?.enabled_features) ? data.enabled_features : [],
    // Note: Backend might send Dict (from recent fix), but our UI component expects Array for checkbox logic. 
    // We will adapt below.
  });


  useEffect(() => {
    // Adapt Data: If enabled_features is a Dict (backend new format), convert to keys array
    let initialEnabled = [];
    if (Array.isArray(data?.enabled_features)) {
      initialEnabled = data.enabled_features;
    } else if (typeof data?.enabled_features === 'object' && data?.enabled_features !== null) {
      initialEnabled = Object.keys(data.enabled_features).filter(k => data.enabled_features[k] === true);
    }
    setForm({ enabled: initialEnabled });

    const loadFeatures = async () => {
      try {
        const response = await apiFetch("/plans/features", token);
        setAvailableFeatures(response);
      } catch (err) {
        console.error("Failed fetching features:", err);
      } finally {
        setLoading(false);
      }
    };
    loadFeatures();
  }, []);

  const toggle = (id) => {
    setForm(prev => ({
      enabled: prev.enabled.includes(id)
        ? prev.enabled.filter(f => f !== id)
        : [...prev.enabled, id]
    }));
  };

  const sanitize = () => {
    // Convert Array back to Dict for the backend (since we updated schema to Dict)
    const featureDict = {};
    // We assume 'availableFeatures' covers the universe of keys. 
    // Or simpler: just map the enabled array to { key: true }
    form.enabled.forEach(key => {
      featureDict[key] = true;
    });
    return { enabled_features: featureDict };
  }

  const handleSave = async () => {
    await onSave(sanitize());
    if (onNext) onNext();
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-xl text-white mb-6">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <DynamicIcon name="Sparkles" /> Feature Entitlements
        </h3>
        <p className="opacity-90">Manage access to premium platform modules.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {availableFeatures.map((feat) => {
          const id = feat.code || feat.featureId;
          const name = feat.name || feat.featureName;
          const desc = feat.description || feat.featureDescription;
          const isEnabled = form.enabled.includes(id);
          return (
            <div
              key={id}
              onClick={() => toggle(id)}
              className={`cursor-pointer p-4 rounded-xl border-2 transition-all flex items-start gap-4 ${isEnabled
                ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                } `}
            >
              <div className={`p-2 rounded-lg ${isEnabled ? "bg-indigo-500 text-white" : "bg-gray-100 text-gray-500"} `}>
                <DynamicIcon name={isEnabled ? "ToggleRight" : "ToggleLeft"} className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h4 className={`font-bold ${isEnabled ? "text-indigo-900 dark:text-indigo-100" : "text-gray-700"} `}>{name}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{desc}</p>
              </div>
              {isEnabled && <DynamicIcon name="Check" className="text-indigo-600 w-5 h-5" />}
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-t dark:border-gray-700 p-4 -mx-6 -mb-6 mt-8 flex justify-end shadow-lg rounded-b-xl">
        <Button onClick={handleSave} className="px-6 shadow-md">
          Save & Next <DynamicIcon name="ArrowRight" className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
